/**
 * Compress an image file before upload
 * - Resizes to max 1200px width/height
 * - Converts to WebP (fallback JPEG) at 0.8 quality
 * - Typically reduces 5MB photos to 200-400KB
 */
export async function compressImage(file, { maxWidth = 1200, maxHeight = 1200, quality = 0.8 } = {}) {
  return new Promise((resolve) => {
    // Skip non-image files
    if (!file.type.startsWith('image/')) { resolve(file); return }
    
    // Skip already small files (under 500KB)
    if (file.size < 500 * 1024) { resolve(file); return }

    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)

      let { width, height } = img

      // Calculate new dimensions maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)

      // Try WebP first, fall back to JPEG
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return }
          const compressed = new File([blob], file.name.replace(/\.\w+$/, '.webp'), {
            type: blob.type,
            lastModified: Date.now(),
          })
          resolve(compressed)
        },
        'image/webp',
        quality
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(file) // fall back to original
    }

    img.src = url
  })
}

/**
 * Compress multiple files
 */
export async function compressImages(files) {
  return Promise.all(Array.from(files).map(f => compressImage(f)))
}
